import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Send } from "lucide-react";
import { getSignal, SIGNALS } from "@/data/signals";
import { SignalChart } from "@/components/signals/SignalChart";
import { Eyebrow, StatusBadge, Direction, BrokerMark } from "@/components/shared/Primitives";
import { Reveal, Stagger, Item } from "@/components/motion/Reveal";
import { fmtDate, fmtPrice } from "@/lib/format";
import NotFound from "@/pages/NotFound";
import { useBrokers } from "@/context/BrokersContext";

const Param = ({ label, value, tone }) => (
  <div className="border-l border-[var(--line)] pl-4"><p className="eyebrow">{label}</p><p className={`mt-1.5 font-mono text-lg ${tone || ""}`}>{value}</p></div>
);

export default function SignalDetail() {
  const { id } = useParams();
  const { brokers: BROKERS } = useBrokers();
  const s = getSignal(id);
  if (!s) return <NotFound />;
  const idx = SIGNALS.findIndex((x) => x.id === id);
  const next = SIGNALS[idx + 1] || SIGNALS[0];
  const execBroker = [...BROKERS].filter((b) => b.swapSource !== "Unavailable").sort((a, b) => a.eurUsdSpread * 10 + a.commission - (b.eurUsdSpread * 10 + b.commission))[0];

  return (
    <div data-testid="signal-detail-page">
      <div className="bg-white border-b border-[var(--line)]">
        <div className="container-x py-10 lg:py-14">
          <Link to="/signals" className="link-arrow text-[var(--ink-3)]" data-testid="signal-back"><ArrowLeft size={14} /> All signals</Link>
          <Reveal className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3"><Direction value={s.direction} /><StatusBadge status={s.status} /><span className="font-mono text-[11px] text-[var(--ink-4)]">{s.id}</span></div>
              <h1 className="mt-4 t-hero">{s.instrument} <span className="text-[var(--ink-3)]">{s.direction.toLowerCase()}</span></h1>
              <p className="mt-5 max-w-2xl t-lead">{s.summary}</p>
            </div>
            <div className="lg:col-span-4 text-sm text-[var(--ink-3)] lg:text-right">
              <p className="eyebrow">Published</p><p className="font-mono mt-1 text-[var(--ink)]">{fmtDate(s.publishedAt)}</p>
              <p className="mt-3 eyebrow">Timeframe · Horizon</p><p className="font-mono mt-1 text-[var(--ink)]">{s.timeframe} · {s.horizon}</p>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="card card-plain mt-10 p-4 sm:p-6"><SignalChart signal={s} /></Reveal>
          <Reveal delay={0.25} className="mt-8 grid grid-cols-2 gap-6 md:grid-cols-5">
            <Param label="Entry conditions" value={s.entry} />
            <Param label="Stop loss" value={fmtPrice(s.stop)} tone="text-[var(--neg)]" />
            <Param label="Target 1" value={fmtPrice(s.targets[0])} tone="text-[var(--pos)]" />
            <Param label="Target 2" value={fmtPrice(s.targets[1])} tone="text-[var(--pos)]" />
            <Param label="Risk / reward" value={s.rr} />
          </Reveal>
        </div>
      </div>

      <Stagger className="container-x py-12 grid grid-cols-1 gap-12 lg:grid-cols-12">
        <div className="lg:col-span-7 space-y-12">
          <Item>
            <Eyebrow className="mb-4">Trade thesis</Eyebrow>
            <div className="space-y-4 text-base leading-relaxed text-[var(--ink-2)]">{s.thesis.map((p, i) => <p key={i}>{p}</p>)}</div>
          </Item>
          <Item>
            <Eyebrow className="mb-4">Invalidation logic</Eyebrow>
            <p className="text-base leading-relaxed text-[var(--ink-2)] border-l-2 border-[var(--neg)] pl-4">{s.invalidation}</p>
          </Item>
          <Item>
            <Eyebrow className="mb-4">Technical levels</Eyebrow>
            <div className="card card-plain overflow-hidden">{s.levels.map((l, i) => <div key={i} className="flex items-center justify-between px-5 py-3 border-b border-[var(--line)] last:border-0 text-sm"><span className="text-[var(--ink-3)]">{l.label}</span><span className="font-mono">{fmtPrice(l.value)}</span></div>)}</div>
          </Item>
          {s.outcome && (
            <Item>
              <Eyebrow className="mb-4">Final outcome</Eyebrow>
              <div className={`card p-6 ${s.outcome.result === "Win" ? "!bg-[#ecfdf5] !border-[#a7f3d0]" : "!bg-[#fff1f2] !border-[#fecdd3]"}`} data-testid="signal-outcome">
                <p className="eyebrow mb-3">{s.outcome.result === "Win" ? "Closed · target reached" : "Closed · stopped"}</p>
                <p className="t-h4">{s.outcome.result} · {s.outcome.r}</p><p className="mt-1 font-mono text-sm text-[var(--ink-3)]">{s.outcome.pips}</p>
              </div>
            </Item>
          )}
        </div>
        <div className="lg:col-span-5 space-y-10">
          <Item>
            <Eyebrow className="mb-4">Status history</Eyebrow>
            <ol className="relative border-l border-[var(--line)] ml-2 space-y-6" data-testid="signal-timeline">
              {s.timeline.map((t, i) => (
                <li key={i} className="pl-6 relative">
                  <span className={`absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full ${i === s.timeline.length - 1 ? "bg-[var(--cobalt)] ring-4 ring-[var(--cobalt-soft)]" : "bg-[var(--line-2)]"}`} />
                  <p className="text-sm font-medium">{t.event}</p>
                  {t.note && <p className="text-sm text-[var(--ink-3)] mt-0.5">{t.note}</p>}
                  <p className="font-mono text-[11px] text-[var(--ink-4)] mt-1">{fmtDate(t.at)}</p>
                </li>
              ))}
            </ol>
            <p className="mt-5 text-xs text-[var(--ink-4)]">The original idea is immutable. Updates are appended with timestamps.</p>
          </Item>
          <Item>
            <Eyebrow className="mb-4">Optional · execution venue</Eyebrow>
            <div className="card p-5 flex items-center gap-4" data-testid="signal-broker-rec">
              <BrokerMark broker={execBroker} size={40} />
              <div className="flex-1"><p className="text-sm font-medium">{execBroker.name}</p><p className="text-xs text-[var(--ink-3)]">Lowest measured all-in cost on majors · {execBroker.eurUsdSpread.toFixed(1)} pips + ${execBroker.commission.toFixed(2)}/lot</p></div>
              <Link to="/brokers/compare" className="link-arrow text-[13px]">Compare <ArrowRight size={14} /></Link>
            </div>
          </Item>
          <Item>
            <a href="https://t.me/forexbrokersignals" target="_blank" rel="noreferrer" className="btn-outline w-full" data-testid="signal-telegram-cta"><Send size={15} /> Get updates on Telegram</a>
          </Item>
        </div>
      </Stagger>

      <div className="container-x pb-20">
        <Link to={`/signals/${next.id}`} className="card card-muted card-tap group flex items-center justify-between p-8 hover:!bg-[var(--cobalt-soft)]" data-testid="signal-next">
          <div><p className="eyebrow">Next idea</p><p className="mt-2 t-h4">{next.instrument} {next.direction.toLowerCase()}</p></div>
          <ArrowRight className="text-[var(--ink-3)] transition-transform group-hover:translate-x-1 group-hover:text-[var(--cobalt)]" />
        </Link>
      </div>
    </div>
  );
}
